import axios from 'axios'
import router from '@/router'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.andonestats.com',
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('dashboard_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRedirecting = false

const AUTH_ERRORS = ['Invalid token', 'User not found', 'Token revoked or expired']

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const msg = error.response?.data?.error

    const isAuthError =
      status === 401 ||
      (status === 403 && AUTH_ERRORS.includes(msg))

    if (isAuthError && !isRedirecting) {
      isRedirecting = true
      localStorage.removeItem('dashboard_token')
      delete api.defaults.headers.common['Authorization']
      setTimeout(() => { isRedirecting = false }, 500)
      router.push({ name: 'login' })
    }
    return Promise.reject(error)
  }
)

export const teamsAPI = {
  getAll: (params = {}) => api.get('/api/dashboard/teams', { params }).then(r => r.data),
  getOne: (uuid) => api.get(`/api/dashboard/teams/${uuid}`).then(r => r.data),
  create: (data) => api.post('/api/dashboard/teams', data).then(r => r.data),
  update: (uuid, data) => api.put(`/api/dashboard/teams/${uuid}`, data).then(r => r.data),
  delete: (uuid) => api.delete(`/api/dashboard/teams/${uuid}`).then(r => r.data),
  uploadLogo: (uuid, file) => {
    const formData = new FormData()
    formData.append('logo', file)
    return api.post(`/api/dashboard/teams/${uuid}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data)
  },
}

export const playersAPI = {
  getAll: (params = {}) => api.get('/api/dashboard/players', { params }).then(r => r.data),
  getOne: (uuid) => api.get(`/api/dashboard/players/${uuid}`).then(r => r.data),
  create: (data) => api.post('/api/dashboard/players', data).then(r => r.data),
  update: (uuid, data) => api.put(`/api/dashboard/players/${uuid}`, data).then(r => r.data),
  delete: (uuid) => api.delete(`/api/dashboard/players/${uuid}`).then(r => r.data),
}

export const gamesAPI = {
  getAll: (params = {}) => api.get('/api/dashboard/games', { params }).then(r => r.data),
  getOne: (uuid) => api.get(`/api/dashboard/games/${uuid}`).then(r => r.data),
  getEvaluation: (uuid) => api.get(`/api/dashboard/games/${uuid}/evaluation`).then(r => r.data),
  saveEvaluation: (uuid, data) => api.post(`/api/dashboard/games/${uuid}/evaluation`, data).then(r => r.data),
  generateEvalReport: (uuid) => api.post(`/api/dashboard/games/${uuid}/evaluation/generate-report`).then(r => r.data),
  getEvalReport: (uuid) => api.get(`/api/dashboard/games/${uuid}/evaluation/report`).then(r => r.data),
  generateShareLink: (uuid) => api.post(`/api/dashboard/games/${uuid}/share-link`).then(r => r.data),
}

export const playerStatsAPI = {
  getByUuid: (uuid) => api.get(`/api/dashboard/players/${uuid}/stats`).then(r => r.data),
}

export const filtersAPI = {
  getAll: () => api.get('/api/dashboard/filters').then(r => r.data),
}

export const galleryAPI = {
  getAll: () => api.get('/api/dashboard/gallery').then(r => r.data),
  upload: (file) => {
    const formData = new FormData()
    formData.append('image', file)
    return api.post('/api/dashboard/gallery', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data)
  },
  delete: (id) => api.delete(`/api/dashboard/gallery/${id}`).then(r => r.data),
}

export const coachesAPI = {
  getAll: (params = {}) => api.get('/api/dashboard/coaches', { params }).then(r => r.data),
  getOne: (id) => api.get(`/api/dashboard/coaches/${id}`).then(r => r.data),
  create: (data) => api.post('/api/dashboard/coaches', data).then(r => r.data),
  update: (id, data) => api.put(`/api/dashboard/coaches/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/api/dashboard/coaches/${id}`).then(r => r.data),
  addTest: (id, data) => api.post(`/api/dashboard/coaches/${id}/tests`, data).then(r => r.data),
  deleteTest: (id, testId) => api.delete(`/api/dashboard/coaches/${id}/tests/${testId}`).then(r => r.data),
  addEntTest: (id, data) => api.post(`/api/dashboard/coaches/${id}/ent-tests`, data).then(r => r.data),
  deleteEntTest: (id, testId) => api.delete(`/api/dashboard/coaches/${id}/ent-tests/${testId}`).then(r => r.data),
  generateCbasReport: (id, testId) => api.post(`/api/dashboard/coaches/${id}/tests/${testId}/generate-report`).then(r => r.data),
  generateEntReport: (id, testId) => api.post(`/api/dashboard/coaches/${id}/ent-tests/${testId}/generate-report`).then(r => r.data),
}

export const analyticsAPI = {
  getAdvanced: (params = {}) => api.get('/api/dashboard/analytics', { params }).then(r => r.data),
  getAccumulated: (params = {}) => api.get('/api/dashboard/analytics/accumulated', { params }).then(r => r.data),
  getEvolution: (params = {}) => api.get('/api/dashboard/analytics/evolution', { params }).then(r => r.data),
}

export const settingsAPI = {
  getProfile: () => api.get('/api/dashboard/auth/me').then(r => r.data),
  changePassword: (newPassword) => api.post('/api/dashboard/auth/change-password', { newPassword }).then(r => r.data),
  forgotPassword: (email) => api.post('/api/dashboard/auth/forgot-password', { email }).then(r => r.data),
}

export const selfEvalAPI = {
  getGrid: (uuid) => api.get(`/api/dashboard/games/${uuid}/self-eval`).then(r => r.data),
  generateReport: (uuid) => api.post(`/api/dashboard/games/${uuid}/self-eval/generate-report`).then(r => r.data),
  getReport: (uuid) => api.get(`/api/dashboard/games/${uuid}/self-eval/report`).then(r => r.data),
}

export const stripeAPI = {
  getPlans: () => api.get('/api/revenuecat/plans').then(r => r.data),
  createCheckout: (priceId) => api.post('/api/stripe/create-checkout', { priceId }).then(r => r.data),
  createPortalSession: () => api.post('/api/stripe/portal').then(r => r.data),
}

export default api
